import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  BillingNotificationsOptIn,
  IPhoneValidationResponse,
  IRequestError,
} from '@patient-ui/shared/models';
import { EnvironmentService } from '@patient-ui/shared-ui/utils';

import { PhoneOptInResponse } from './phone.actions';

@Injectable({
  providedIn: 'root',
})
export class PhoneService {
  constructor(
    private readonly http: HttpClient,
    private envService: EnvironmentService
  ) {}

  billingNotificationsOptIn(
    optInData: BillingNotificationsOptIn
  ): Observable<PhoneOptInResponse> {
    /*
     * The backend expects mobile consent to be a Y/N string instead of a boolean. Making that switch
     * here so we can keep the convenience of using a boolean for boolean data while still supporting
     * what the backend expects.
     * Keeping
     */
    const optInBody = {
      ...optInData,
      mobileConsent: optInData.mobileConsent ? `Y` : `N`,
    };
    return this.http
      .post<PhoneOptInResponse | null>(
        `${this.envService.baseUrl}/guest/billing/optin`,
        optInBody
      )
      .pipe(
        map((res: PhoneOptInResponse | null) => {
          if (res) {
            return res;
          } else {
            const defaultResponse: PhoneOptInResponse = {
              optedIn: false,
              message: 'successfully ',
            };
            return defaultResponse;
          }
        })
      );
  }

  validatePhone(phoneNumber: string): Observable<IPhoneValidationResponse> {
    const formattedPhoneNumber = phoneNumber.replace(/\D/g, '');
    const validatePhoneUrl = `${this.envService.baseUrl}/guest/phoneNumber?phoneNumber=${formattedPhoneNumber}`;

    return this.http.get<IPhoneValidationResponse>(validatePhoneUrl);
  }

  getPhoneType(type: string): string {
    let phoneType: string;

    switch (type) {
      case 'undetermined':
      case 'voip': {
        phoneType = 'unknown';
        break;
      }
      case 'landline': {
        phoneType = 'home';
        break;
      }
      case 'freephone':
      case 'other': {
        phoneType = 'work';
        break;
      }
      default: {
        phoneType = type;
      }
    }
    return phoneType;
  }

  getPhoneDisplayText(value: string): string {
    let displayText: string;

    switch (value.toLowerCase()) {
      case 'mobile': {
        displayText = 'Mobile';
        break;
      }
      case 'home': {
        displayText = 'LandLine';
        break;
      }
      case 'unknown': {
        displayText = 'Undetermined';
        break;
      }
      case 'work': {
        displayText = 'Other';
        break;
      }
      default: {
        displayText = 'Other';
      }
    }
    return displayText;
  }

  /**
   * Gets user-friendly error message for phone validation failures.
   *
   * @param error - The request error from phone validation API
   * @returns Formatted error message for display to user
   */
  getPhoneErrorMessage(error: IRequestError | null): string {
    if (error?.code === 'Phone.Duplicate') {
      return 'Your primary phone number and secondary phone number cannot be the same.';
    }
    return 'We are having trouble validating this phone number. Please try again.';
  }

  /**
   * Update existing phone number or create new one
   * If phoneId is provided, updates existing phone record via PUT
   * Otherwise creates new phone record via POST
   */
  updatePhone(
    phoneNumber: string,
    phoneId?: number | null,
    phoneType: string = 'MOBILE'
  ) {
    const payload = {
      id: phoneId || null,
      isPrimary: true,
      number: phoneNumber,
      type: phoneType.toUpperCase(),
      usePrimaryPhoneOfPrimaryPatient: false,
    };

    // If phoneId exists, use PUT to update existing record
    if (phoneId) {
      return this.http.put(
        `${this.envService.baseUrl}/protected/patients/current/phones/${phoneId}`,
        payload
      );
    }

    // Otherwise use POST to create new record
    return this.http.post(
      `${this.envService.baseUrl}/protected/patients/current/phones`,
      payload
    );
  }
}
